import { useState, useCallback, useEffect, useRef } from 'react';
import { log } from '@/lib/utils/logger';

export type FieldUpdateMode = 'debounce' | 'blur';

interface UseFieldUpdateOptions<T> {
  initialValue: T;
  updateStore: (value: T) => void;
  mode?: FieldUpdateMode;
  delay?: number; // For debounce mode
}

/**
 * Hook that always overwrites local value when initialValue changes
 * Compatible with React 19 / Next 16 ESLint rules
 */
export function useFieldUpdate<T>({
  initialValue,
  updateStore,
  mode = 'debounce',
  delay = 500,
}: UseFieldUpdateOptions<T>) {
  const [editedState, setEditedState] = useState<{ base: T; value: T } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isModified = editedState != null && Object.is(editedState.base, initialValue);
  const localValue = isModified ? editedState.value : initialValue;

  // Handle debounced updates
  useEffect(() => {
    if (mode !== 'debounce' || !isModified) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      log.debug('[useFieldUpdate] Debounced update:', localValue);
      updateStore(localValue);
      setEditedState(null);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [localValue, isModified, updateStore, delay, mode]);

  const handleChange = useCallback(
    (newValue: T) => {
      setEditedState({ base: initialValue, value: newValue });
    },
    [initialValue]
  );

  const handleBlur = useCallback(() => {
    // Both blur and debounce modes commit on blur
    if ((mode === 'blur' || mode === 'debounce') && isModified) {
      // Cancel pending timer in debounce mode since we're committing now
      if (mode === 'debounce' && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      log.debug('[useFieldUpdate] Blur update:', localValue);
      updateStore(localValue);
      setEditedState(null);
    }
  }, [mode, isModified, localValue, updateStore]);

  return {
    value: localValue,
    onChange: handleChange,
    onBlur: handleBlur,
    isModified,
  };
}
